export interface BlogPost {
  title: string
  slug: string
  description: string
  date: string
  author: string
  category: string
  tags: string[]
  keywords: string
  featured_image?: string
  content: string
}

interface FrontmatterResult {
  data: Record<string, unknown>
  content: string
}

function parseFrontmatter(raw: string): FrontmatterResult {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/)
  if (!match) return { data: {}, content: raw }

  const yaml = match[1]
  const content = match[2].trim()
  const data: Record<string, unknown> = {}

  for (const line of yaml.split('\n')) {
    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) continue
    const key = line.slice(0, colonIdx).trim()
    let value: unknown = line.slice(colonIdx + 1).trim()

    // Remove surrounding quotes
    if (typeof value === 'string' && value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1)
    }

    // Parse arrays like ["tag1", "tag2"]
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        value = JSON.parse(value)
      } catch {
        // keep as string
      }
    }

    data[key] = value
  }

  return { data, content }
}

// Import all .md files from content/blog/ at build time
const modules = import.meta.glob('../content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

function loadPosts(): BlogPost[] {
  const posts: BlogPost[] = []

  for (const [, raw] of Object.entries(modules)) {
    const { data, content } = parseFrontmatter(raw)
    if (!data.title || !data.slug) continue

    posts.push({
      title: data.title as string,
      slug: data.slug as string,
      description: (data.description as string) || '',
      date: (data.date as string) || '',
      author: (data.author as string) || 'Garrett John Law',
      category: (data.category as string) || '',
      tags: (data.tags as string[]) || [],
      keywords: (data.keywords as string) || '',
      featured_image: data.featured_image as string | undefined,
      content,
    })
  }

  return posts.sort((a, b) => b.date.localeCompare(a.date))
}

export const blogPosts = loadPosts()

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug)
}
